import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../utils/api';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { toast } from 'sonner';
import { format } from 'date-fns';
import './UserDetail.css';

export const UserDetail = () => {
  const { i18n } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const isGerman = i18n.language === 'de';
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    mpg_username: '',
    phone_number: '',
    sponsor_name: '',
    shipping_location: 'england',
    ship_to_friend_username: '',
    street: '',
    house_no: '',
    zip: '',
    city: '',
    country: '',
    address_additional: '',
  });

  useEffect(() => {
    if (userId) {
      loadUserData();
    }
  }, [userId]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const userResponse = await adminAPI.getUserById(userId);
      const userData = userResponse.data;
      
      setUser(userData);
      
      // Load logs
      try {
        const logsResponse = await adminAPI.getUserLogs(userId);
        setLogs(logsResponse.data || []);
      } catch {
        setLogs([]);
      }
      
      // Set form data
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        mpg_username: userData.mpg_username || '',
        phone_number: userData.phone_number || '',
        sponsor_name: userData.sponsor_name || '',
        shipping_location: userData.shipping_location || 'england',
        ship_to_friend_username: userData.ship_to_friend_username || '',
        street: userData.address?.street || '',
        house_no: userData.address?.house_no || '',
        zip: userData.address?.zip || '',
        city: userData.address?.city || '',
        country: userData.address?.country || '',
        address_additional: userData.address?.address_additional || '',
      });
      
    } catch (error) {
      console.error('Error loading user:', error);
      toast.error(isGerman ? 'Fehler beim Laden' : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddressSelect = (address) => {
    setFormData({
      ...formData,
      street: address.street,
      house_no: address.house_no,
      zip: address.zip,
      city: address.city,
      country: address.country,
    });
    setAddressSelected(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        mpg_username: formData.mpg_username,
        phone_number: formData.phone_number || null,
        sponsor_name: formData.sponsor_name,
        shipping_location: formData.shipping_location,
        ship_to_friend_username: formData.ship_to_friend_username || null,
        address: {
          street: formData.street,
          house_no: formData.house_no,
          zip: formData.zip,
          city: formData.city,
          country: formData.country,
          address_additional: formData.address_additional || null,
        }
      };

      await adminAPI.updateUserProfile(userId, updateData);
      toast.success(isGerman ? 'Benutzer aktualisiert' : 'User updated');
      setEditMode(false);
      setAddressSelected(false);
      loadUserData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Update failed';
      toast.error(message);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        mpg_username: user.mpg_username || '',
        phone_number: user.phone_number || '',
        sponsor_name: user.sponsor_name || '',
        shipping_location: user.shipping_location || 'england',
        ship_to_friend_username: user.ship_to_friend_username || '',
        street: user.address?.street || '',
        house_no: user.address?.house_no || '',
        zip: user.address?.zip || '',
        city: user.address?.city || '',
        country: user.address?.country || '',
        address_additional: user.address?.address_additional || '',
      });
    }
    setEditMode(false);
    setAddressSelected(false);
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (!user) {
    return <div className="error-container">User not found</div>;
  }

  const canEdit = !user.roles?.includes('superadmin');

  return (
    <div className="user-detail-page">
      <div className="user-detail-container">
        <button onClick={() => navigate('/admin')} className="back-button">
          ← {isGerman ? 'Zurück zum Admin Dashboard' : 'Back to Admin Dashboard'}
        </button>

        <div className="detail-header">
          <h1>{isGerman ? 'Benutzerdetails' : 'User Details'}</h1>
          {canEdit && !editMode && (
            <button onClick={() => setEditMode(true)} className="edit-button">
              ✏️ {isGerman ? 'BEARBEITEN' : 'EDIT USER'}
            </button>
          )}
        </div>

        <div className="detail-card">
          {!editMode ? (
            <>
              <h2>{user.first_name} {user.last_name}</h2>
              <div className="user-info-grid">
                <div className="info-item">
                  <span className="label">MPG Username:</span>
                  <span className="value"><code>{user.mpg_username}</code></span>
                </div>
                <div className="info-item">
                  <span className="label">{isGerman ? 'E-Mail' : 'Email'}:</span>
                  <span className="value">{user.email}</span>
                </div>
                {user.phone_number && (
                  <div className="info-item">
                    <span className="label">{isGerman ? 'Telefonnummer' : 'Phone'}:</span>
                    <span className="value">{user.phone_number}</span>
                  </div>
                )}
                <div className="info-item">
                  <span className="label">Sponsor:</span>
                  <span className="value">{user.sponsor_name}</span>
                </div>
                <div className="info-item">
                  <span className="label">{isGerman ? 'Versandoption' : 'Shipping'}:</span>
                  <span className="value">{user.shipping_location === 'england' ? '🇬🇧 England' : '🇵🇭 Philippinen'}</span>
                </div>
                {user.ship_to_friend_username && (
                  <div className="info-item">
                    <span className="label">{isGerman ? 'Versand an Freund' : 'Ship to Friend'}:</span>
                    <span className="value">@{user.ship_to_friend_username}</span>
                  </div>
                )}
              </div>

              <div className="address-section">
                <h3>{isGerman ? 'Aktuelle Adresse' : 'Current Address'}</h3>
                <p>{user.address?.street} {user.address?.house_no}</p>
                {user.address?.address_additional && (
                  <p style={{ color: '#005F6B', fontStyle: 'italic' }}>↳ {user.address.address_additional}</p>
                )}
                <p>{user.address?.zip} {user.address?.city}</p>
                <p>{user.address?.country}</p>
              </div>
            </>
          ) : (
            <form onSubmit={handleUpdate} className="admin-edit-form">
              <h3>{isGerman ? 'Benutzer bearbeiten' : 'Edit User'}</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{isGerman ? 'Vorname' : 'First Name'}</label>
                  <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>{isGerman ? 'Nachname' : 'Last Name'}</label>
                  <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label>MPG Username</label>
                <input type="text" name="mpg_username" value={formData.mpg_username} onChange={handleChange} />
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Telefonnummer' : 'Phone Number'}</label>
                <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder="+49 123 456789" />
              </div>

              <div className="form-group">
                <label>Sponsor</label>
                <input type="text" name="sponsor_name" value={formData.sponsor_name} onChange={handleChange} />
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Versandoption' : 'Shipping'}</label>
                <select name="shipping_location" value="england" disabled>
                  <option value="england">🇬🇧 England</option>
                </select>
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Versand an Freund' : 'Ship to Friend'}</label>
                <input type="text" name="ship_to_friend_username" value={formData.ship_to_friend_username} onChange={handleChange} placeholder="MPG Username" />
              </div>

              <h4 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{isGerman ? 'Adresse' : 'Address'}</h4>

              <AddressAutocomplete 
                onAddressSelect={handleAddressSelect}
                initialValue={`${user.address?.street || ''} ${user.address?.house_no || ''}, ${user.address?.zip || ''} ${user.address?.city || ''}`}
              />

              <div className="form-group">
                <label>{isGerman ? 'Adresszusatz' : 'Additional Info'}</label>
                <input type="text" name="address_additional" value={formData.address_additional} onChange={handleChange} placeholder={isGerman ? 'Etage, Hinweis...' : 'Floor, Note...'} />
              </div>

              {addressSelected && (
                <div className="address-preview">
                  <h5>{isGerman ? 'Neue Adresse' : 'New Address'}</h5>
                  <p>{formData.street} {formData.house_no}</p>
                  {formData.address_additional && <p>↳ {formData.address_additional}</p>}
                  <p>{formData.zip} {formData.city}, {formData.country}</p>
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={handleCancel} className="cancel-btn">
                  {isGerman ? 'Abbrechen' : 'Cancel'}
                </button>
                <button type="submit" className="save-btn">
                  💾 {isGerman ? 'SPEICHERN' : 'SAVE'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="logs-card">
          <h2>{isGerman ? '📋 Änderungs-Protokolle' : '📋 Change Logs'}</h2>
          
          {loading ? (
            <p className="no-logs">Loading logs...</p>
          ) : logs.length === 0 ? (
            <p className="no-logs">{isGerman ? 'Keine Protokolle vorhanden' : 'No logs available'}</p>
          ) : (
            <div className="logs-timeline">
              {logs.map((log) => (
                <div key={log._id} className="log-entry">
                  <div className="log-header">
                    <span className="log-date">
                      {new Date(log.changed_at).toLocaleString('de-DE')}
                    </span>
                    <span className="log-author">
                      {isGerman ? 'Geändert von' : 'Changed by'}: {log.changed_by || 'Unknown'}
                    </span>
                  </div>
                  
                  {log.diff && Object.keys(log.diff).length > 0 && (
                    <div className="log-changes">
                      <h4>{isGerman ? 'Änderungen:' : 'Changes:'}</h4>
                      {Object.entries(log.diff).map(([field, change]) => (
                        <div key={field} className="change-item">
                          <span className="field-name">{field}:</span>
                          <span className="old-value">'{String(change.from || '')}'</span>
                          <span className="arrow">→</span>
                          <span className="new-value">'{String(change.to || '')}'</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
